// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Gift } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface GiftMembershipDialogProps {
  userId: string;
  userName: string;
  onSuccess?: () => void;
}

const GiftMembershipDialog: React.FC<GiftMembershipDialogProps> = ({
  userId,
  userName,
  onSuccess
}) => {
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState('');
  const [duration, setDuration] = useState('1');
  const [durationUnit, setDurationUnit] = useState('month');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');

  // Premium memberships
  const premiumMemberships = [
    { slug: 'athlete_premium', name: 'Athlete Premium' },
    { slug: 'athlete_hof', name: 'Hall of Fame - Tommy Canale' },
    { slug: 'athlete_mvp', name: 'MVP - Tommy Canale' },
    { slug: 'athlete_last_chance', name: 'Last Chance - Tommy Canale' },
    { slug: 'event_press', name: 'Event Press' },
    { slug: 'illinois_press', name: 'Illinois Press' },
    { slug: 'iowa_press', name: 'Iowa Press' },
    { slug: 'press_ks', name: 'Kansas Press' },
    { slug: 'press_mo', name: 'Missouri Press' },
    { slug: 'oklahoma_press', name: 'Oklahoma Press' },
    { slug: 'wisconsin_press', name: 'Wisconsin Press' },
    { slug: 'womens_press', name: 'Women\'s Press' },
    { slug: 'vendor_press', name: 'Vendor Press' }
  ];

  const durationOptions = [
    { value: '1', label: '1' },
    { value: '3', label: '3' },
    { value: '6', label: '6' },
    { value: '12', label: '12' },
    { value: 'custom', label: 'Custom' }
  ];

  // Calculate end date when duration or start date changes
  React.useEffect(() => {
    if (startDate && duration !== 'custom') {
      const start = new Date(startDate);
      let end = new Date(start);
      
      if (durationUnit === 'month') {
        end.setMonth(end.getMonth() + parseInt(duration));
      } else if (durationUnit === 'year') {
        end.setFullYear(end.getFullYear() + parseInt(duration));
      }
      
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [startDate, duration, durationUnit]);

  const getMembershipName = (slug: string) => {
    switch (slug) {
      case 'athlete_premium': return 'Athlete Premium';
      case 'athlete_hof': return 'Hall of Fame - Tommy Canale';
      case 'athlete_mvp': return 'MVP - Tommy Canale';
      case 'athlete_last_chance': return 'Last Chance - Tommy Canale';
      case 'event_press': return 'Event Press';
      case 'illinois_press': return 'Illinois Press';
      case 'iowa_press': return 'Iowa Press';
      case 'press_ks': return 'Kansas Press';
      case 'press_mo': return 'Missouri Press';
      case 'oklahoma_press': return 'Oklahoma Press';
      case 'wisconsin_press': return 'Wisconsin Press';
      case 'womens_press': return 'Women\'s Press';
      case 'vendor_press': return 'Vendor Press';
      default: return 'Premium Membership';
    }
  };

  const handleSubmit = async () => {
    if (!selectedMembership || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!profile?.id) {
      toast.error('Admin profile not found');
      return;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('profile_memberships')
        .insert({
          profile_id: userId,
          membership_slug: selectedMembership,
          is_active: true,
          assigned_by: profile.id,
          assigned_at: new Date().toISOString(),
          started_at: new Date(startDate).toISOString(),
          expires_at: new Date(endDate).toISOString(),
          gift_notification_sent: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error gifting membership:', error);
        toast.error('Failed to gift membership');
        return;
      }

      toast.success(`Successfully gifted ${getMembershipName(selectedMembership)} to ${userName}`);
      setIsOpen(false);
      onSuccess?.();
      
      // Reset form
      setSelectedMembership('');
      setDuration('1');
      setDurationUnit('month');
      setStartDate(new Date().toISOString().split('T')[0]);
      setEndDate('');
      setNote('');
    } catch (err) {
      console.error('Error gifting membership:', err);
      toast.error('Failed to gift membership');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
          <Gift className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-orange-600" />
            Gift Membership to {userName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="membership">Membership Type</Label>
            <Select value={selectedMembership} onValueChange={setSelectedMembership}>
              <SelectTrigger>
                <SelectValue placeholder="Select a premium membership" />
              </SelectTrigger>
              <SelectContent>
                {premiumMemberships.map((membership) => (
                  <SelectItem key={membership.slug} value={membership.slug}>
                    {membership.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {durationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="unit">Unit</Label>
              <Select value={durationUnit} onValueChange={setDurationUnit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month(s)</SelectItem>
                  <SelectItem value="year">Year(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={duration !== 'custom'}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note (Optional)</Label>
            <Textarea
              placeholder="Reason for gifting membership..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? 'Gifting...' : 'Gift Membership'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GiftMembershipDialog;